/**
 * Forgot Password
 *
 * Sends a Firebase password-reset email and confirms in-place. Always shows
 * a success state regardless of whether the email exists, to avoid account
 * enumeration.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/Toast';
import { AuthShell } from '@/components/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPassword() {
  const { resetPassword } = useAuth();
  const { error: showError } = useToast();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !EMAIL_RE.test(email)) {
      showError('Check your input', 'Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(email);
      // Always show success — do not leak whether the email exists.
      setSent(true);
    } catch {
      // Likewise, do not surface the underlying Firebase code.
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={sent ? 'Check your inbox' : 'Reset your password'}
      subtitle={
        sent
          ? "If an account exists for that address, we've sent a reset link."
          : "Enter your email and we'll send you a reset link."
      }
      footer={
        <Link
          to="/login"
          className="inline-flex items-center gap-1 font-medium text-foreground underline-offset-4 hover:underline"
        >
          <ArrowLeft className="size-3.5" />
          Back to sign in
        </Link>
      }
    >
      {sent ? (
        <div className="space-y-4 text-center">
          <span className="mx-auto grid size-10 place-items-center rounded-full bg-success/10 text-success">
            <CheckCircle2 className="size-5" />
          </span>
          <p className="text-sm text-muted-foreground">
            Open the email and follow the link to create a new password. The
            link expires in 1 hour.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSent(false);
              setEmail('');
            }}
          >
            Send to a different email
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="reset-email">Email</Label>
            <Input
              id="reset-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              disabled={loading}
            />
          </div>
          <Button type="submit" size="lg" className="w-full" disabled={loading}>
            {loading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Mail className="size-4" />
            )}
            {loading ? 'Sending…' : 'Send reset link'}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
